import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Plus, Trash2, Eye, Users, UserCheck, UserPlus, Download, FileText } from 'lucide-react';
import { useAuth } from '../../Contexts/AuthContext/AuthContext';
import { exportToCSV, exportToPDF, formatDataForExport } from '../../utils/exportUtils';
import {
  fetchProjects,
  createProject,
  updateProject,
  deleteProject,
  joinProject,
  leaveProject,
  getProjectParticipantCount,
  checkProjectJoinStatus,
  Project
} from '../../services/api';


interface SharedProjectListProps {
  isAdmin?: boolean;
  maxItems?: number; 
  showCreateButton?: boolean;
  className?: string;
}

const SharedProjectList: React.FC<SharedProjectListProps> = ({ 
  isAdmin = false, 
  maxItems,
  showCreateButton = false,
  className = "projects-container"
}) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDesc, setNewProjectDesc] = useState('');
  const [newProjectStatus, setNewProjectStatus] = useState('Unassigned');
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null); // For individual project actions
  const [showStatusUpdate, setShowStatusUpdate] = useState<number | null>(null); // For status update dropdown
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null); // For status update loading
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { volunteerId } = useAuth();

  const isAdminRoute = location.pathname.includes('/admin');

  // Get volunteer ID from AuthContext with fallback
  const getVolunteerId = React.useCallback((): string => {
    return volunteerId ? volunteerId.toString() : '1'; // Fallback to 1 for development
  }, [volunteerId]);

  // Fetch projects based on user type
  const fetchProjectsData = React.useCallback(async () => {
    try {
      setLoading(true);
      console.log('Fetching projects...');
      
      let projectsData;
      if (isAdminRoute) {
        // Admin can see all projects
        projectsData = await fetchProjects('admin');
      } else if (volunteerId) {
        // Volunteer can see Active projects and Completed projects they participated in
        projectsData = await fetchProjects('volunteer', volunteerId);
      } else {
        // Public can only see Active projects
        projectsData = await fetchProjects('public');
      }
      
      console.log('Projects data:', projectsData);
      
      // No need for manual filtering as backend handles it
      const filteredProjects = projectsData;

      // Fetch participant counts and join status for each project
      const projectsWithParticipants = await Promise.all(
        filteredProjects.map(async (project) => {
          try {
            // Get participant count
            const countData = await getProjectParticipantCount(project.id);
            
            // Get join status for the current volunteer
            const volunteerId = getVolunteerId();
            let is_joined = false;
            
            if (volunteerId) {
              try {
                const joinStatus = await checkProjectJoinStatus(project.id, volunteerId);
                is_joined = joinStatus.is_joined;
              } catch (joinError) {
                console.error('Error checking join status:', joinError);
                is_joined = false;
              }
            }

            return {
              ...project,
              participant_count: countData.count,
              is_joined: is_joined
            };
          } catch (error) {
            console.error('Error fetching project data:', error);
            return { ...project, participant_count: 0, is_joined: false };
          }
        })
      );

      if (maxItems) {
        setProjects(projectsWithParticipants.slice(0, maxItems));
      } else {
        setProjects(projectsWithParticipants);
      }
    } catch (error: any) {
      console.error('Error fetching projects:', error);
      // You could show a toast notification here instead of alert
      alert(error.message || 'Failed to fetch projects. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [isAdminRoute, maxItems, volunteerId, getVolunteerId]);

  useEffect(() => {
    fetchProjectsData();
  }, [fetchProjectsData]);

  const handleCreateProject = async () => {
    try {
      const newProject = await createProject(newProjectName, newProjectDesc, newProjectStatus);
      
      console.log('Project created successfully:', newProject);
      
      setProjects([...projects, newProject]);
      setNewProjectName('');
      setNewProjectDesc('');
      setNewProjectStatus('Unassigned');
      setShowProjectForm(false);
    } catch (error: any) {
      console.error('Error creating project:', error);
      alert(error.message || 'Failed to create project. Please try again.');
    }
  };

  const handleDeleteProject = async (projectId: number) => {
    if (window.confirm('Are you sure you want to delete this project? All related tasks will also be deleted.')) {
      try {
        setActionLoading(projectId);
        await deleteProject(projectId);
        setProjects(projects.filter(p => p.id !== projectId));
        if (selectedProject?.id === projectId) {
          setSelectedProject(null);
        }
      } catch (error: any) {
        console.error('Error deleting project:', error);
        alert(error.message || 'Failed to delete project. Please try again.');
      } finally {
        setActionLoading(null);
      }
    }
  };

  const handleStatusUpdate = async (projectId: number, newStatus: 'Active' | 'Completed' | 'Unassigned') => {
    try {
      setUpdatingStatus(projectId);
      await updateProject(projectId, { status: newStatus });
      
      // Update the project in the local state
      setProjects(projects.map(project => 
        project.id === projectId 
          ? { ...project, status: newStatus }
          : project
      ));
      
      setShowStatusUpdate(null);
      alert('Project status updated successfully!');
    } catch (error: any) {
      console.error('Error updating project status:', error);
      alert(error.message || 'Failed to update project status. Please try again.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleViewProject = (project: Project) => {
    setSelectedProject(prev => 
      prev?.id === project.id ? null : project
    );
  };

  const handleActionButton = (project: Project) => {
    if (isAdminRoute) {
      navigate(`/admin/manage-records/projects/${project.id}/tasks`);
    } else if (project.is_joined) {
      navigate(`/volunteer/projects/${project.id}/tasks`);
    } else {
      handleJoinProject(project);
    }
  };

  const handleJoinProject = async (project: Project) => {
    try {
      const volunteerId = getVolunteerId();
      
      setActionLoading(project.id);
      await joinProject(project.id, volunteerId);

      // Update the local state to reflect the join status
      setProjects(prevProjects => 
        prevProjects.map(p => 
          p.id === project.id 
            ? { ...p, is_joined: true, participant_count: (p.participant_count || 0) + 1 }
            : p
        )
      );
      
      alert(`Successfully joined "${project.name}"!`);
    } catch (error: any) {
      console.error('Error joining project:', error);
      alert(error.message || 'Failed to join project. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleLeaveProject = async (project: Project) => {
    try {
      const volunteerId = getVolunteerId();
      
      setActionLoading(project.id);
      await leaveProject(project.id, volunteerId);

      // Update the local state to reflect the leave status
      setProjects(prevProjects => 
        prevProjects.map(p => 
          p.id === project.id 
            ? { ...p, is_joined: false, participant_count: Math.max(0, (p.participant_count || 1) - 1) }
            : p
        )
      );
      
      alert(`Left "${project.name}" successfully.`);
    } catch (error: any) {
      console.error('Error leaving project:', error);
      alert(error.message || 'Failed to leave project. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  // Helper function to check if an action is loading for a specific project
  const isActionLoading = (projectId: number): boolean => {
    return actionLoading === projectId;
  };

  // Export functions
  const handleExportCSV = () => {
    const formattedData = formatDataForExport(projects, 'projects');
    exportToCSV(formattedData, `projects_${new Date().toISOString().split('T')[0]}`);
  };

  const handleExportPDF = async () => {
    setExportLoading(true);
    try {
      const formattedData = formatDataForExport(projects, 'projects');
      await exportToPDF(formattedData, `projects_${new Date().toISOString().split('T')[0]}`, 'Projects List');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className={className}>
      {isAdminRoute && showCreateButton && (
        <div className="projects-header">
          <h2>Admin Projects</h2>
          <div className="projects-controls">
            <div className="export-buttons">
              <button
                onClick={handleExportCSV}
                className="export-btn csv-btn"
                disabled={projects.length === 0}
              >
                <Download size={16} />
                Export CSV
              </button>
              <button
                onClick={handleExportPDF}
                className="export-btn pdf-btn"
                disabled={projects.length === 0 || exportLoading}
              >
                <FileText size={16} />
                {exportLoading ? 'Exporting...' : 'Export PDF'}
              </button>
            </div>
            <button 
              className="create-project-btn"
              onClick={() => setShowProjectForm(true)}
            >
              <Plus size={18} /> New Project
            </button>
          </div>
        </div>
      )}

      {showProjectForm && isAdminRoute && (
        <div className="project-form">
          <input
            type="text"
            placeholder="Project name"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
          />
          <textarea
            placeholder="Project description"
            value={newProjectDesc}
            onChange={(e) => setNewProjectDesc(e.target.value)}
          />
          <div className="status-field">
            <label htmlFor="project-status">Status:</label>
            <select
              id="project-status"
              value={newProjectStatus}
              onChange={(e) => setNewProjectStatus(e.target.value)}
            >
              <option value="Unassigned">Unassigned (Draft)</option>
              <option value="Active">Active</option>
              <option value="Completed">Completed</option>
            </select>
          </div>
          <div className="form-actions">
            <button onClick={handleCreateProject}>Create</button>
            <button onClick={() => setShowProjectForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="projects-list">
        {loading ? (
          <div className="loading">Loading projects...</div>
        ) : projects.length === 0 ? (
          <div className="no-projects">
            {isAdminRoute ? 'No projects found' : 'No active projects available'}
          </div>
        ) : (
          projects.map(project => (
            <div key={project.id} className={`${maxItems ? "project-card-mini" : "project-card"} status-${project.status}`}>
              <div className={maxItems ? "project-header-mini" : "project-header"}>
                <div className={maxItems ? "project-title-mini" : "project-title"}>
                  {maxItems ? <h4>{project.name}</h4> : <h3>{project.name}</h3>}
                  <span className={`status-badge ${project.status}`}>
                    {project.status}
                  </span>
                </div>
                
                {/* Participant count */}
                <div className="participant-count">
                  <Users size={14} />
                  <span>{project.participant_count || 0}</span>
                </div>

                <div className={maxItems ? "project-actions-mini" : "project-actions"}>
                  <button 
                    onClick={() => handleViewProject(project)} 
                    className="view-btn"
                    disabled={isActionLoading(project.id)}
                  >
                    <Eye size={maxItems ? 14 : 16} /> View
                  </button>
                  
                  {isAdminRoute && !maxItems && (
                    <button 
                      onClick={() => handleDeleteProject(project.id)} 
                      className="delete-btn"
                      disabled={isActionLoading(project.id)}
                    >
                      {isActionLoading(project.id) ? (
                        'Deleting...'
                      ) : (
                        <Trash2 size={16} />
                      )}
                    </button>
                  )}
                </div>
              </div>

              {selectedProject?.id === project.id && (
                <div className={maxItems ? "project-details-mini" : "project-details"}>
                  <p>{project.description}</p>
                  <div className={maxItems ? "project-meta-mini" : "project-meta"}>
                    <span>Created: {new Date(project.created_at).toLocaleDateString()}</span>
                    
                    {/* Join/Leave buttons for volunteers */}
                    {!isAdminRoute && (
                      <>
                        <Link 
                          to={`/volunteer/projects/${project.id}/tasks`}
                          className="view-tasks-btn"
                        >
                          View Tasks
                        </Link>
                        {project.is_joined ? (
                          <button 
                            onClick={() => handleLeaveProject(project)}
                            className="leave-project-btn"
                            disabled={isActionLoading(project.id)}
                          >
                            {isActionLoading(project.id) ? (
                              'Leaving...'
                            ) : (
                              <>
                                <UserCheck size={14} /> Leave Project
                              </>
                            )}
                          </button>
                        ) : (
                          <button 
                            onClick={() => handleJoinProject(project)}
                            className="join-project-btn"
                            disabled={isActionLoading(project.id)}
                          >
                            {isActionLoading(project.id) ? (
                              'Joining...'
                            ) : (
                              <>
                                <UserPlus size={14} /> Join Project
                              </>
                            )}
                          </button>
                        )}
                      </>
                    )}
                    
                    {/* Admin view tasks button */}
                    {isAdminRoute && (
                      <button 
                        onClick={() => handleActionButton(project)}
                        className="see-tasks-btn"
                        disabled={isActionLoading(project.id)}
                      >
                        View Tasks
                      </button>
                    )}
                    
                    {/* Admin project status button */}
                    {isAdminRoute && (
                      <button 
                        onClick={() => setShowStatusUpdate(showStatusUpdate === project.id ? null : project.id)}
                        className="project-status-btn"
                        disabled={updatingStatus === project.id}
                      >
                        {updatingStatus === project.id ? 'Updating...' : 'Project Status'}
                      </button>
                    )}
                    
                    {/* Status update dropdown */}
                    {isAdminRoute && showStatusUpdate === project.id && (
                      <div className="status-update-dropdown">
                        <h4>Update Project Status</h4>
                        <div className="status-options">
                          {(['Unassigned', 'Active', 'Completed'] as const).map(status => (
                            <button
                              key={status}
                              onClick={() => handleStatusUpdate(project.id, status)}
                              className={`status-option ${project.status === status ? 'current-status' : ''}`}
                              disabled={updatingStatus === project.id}
                            >
                              {status}
                              {project.status === status && <span className="current-indicator">(Current)</span>}
                            </button>
                          ))}
                        </div>
                        <button 
                          onClick={() => setShowStatusUpdate(null)}
                          className="cancel-status-btn"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SharedProjectList;
